import React, { useState, useEffect } from 'react';
import { formatTime12 } from '../utils/timeZone';
import axios from 'axios';
import { formatDateTime } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Settings, 
  MapPin, 
  Clock,
  Bell, 
  FileText, 
  Plus,
  Edit,
  Loader2,
  Save,
  ExternalLink,
  Copy,
  CheckCircle,
  AlertTriangle,
  MessageSquare,
  Send,
  RefreshCw,
  Calendar,
  Phone,
  Globe,
  Play
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FoodPantryAdminSettingsPage = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    kiosk_enabled: true,
    verification_field: 'date_of_birth',
    service_days: [0, 1, 2], // Mon, Tue, Wed
    duplicate_window_minutes: 30,
    form_reset_seconds: 10,
    confirmation_message: '',
    // Nashville settings
    nashville: {
      lobby_display_message: "Please meet us at the door to have assistance taking your box to your car",
      alert_duration: 60
    },
    // Charlotte settings
    charlotte: {
      lobby_display_message: "Please meet us at the door to have assistance taking your box to your car",
      alert_duration: 60,
      pantry_staff_alert_message: "Attention Pantry Staff, you have a shopper coming to the pantry"
    },
    // Legacy fields for backward compatibility
    lobby_display_message: "Please meet us at the door to have assistance taking your box to your car",
    now_serving_alert_seconds: 60
  });

  // Location dialog
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationForm, setLocationForm] = useState({
    name: '',
    address: '',
    is_active: true,
    teams_webhook_url: '',
    eligibility_rule: 'monthly',
    operational_mode: 'nashville'
  });
  const [savingLocation, setSavingLocation] = useState(false);

  // SMS Notification state
  const [smsSettings, setSmsSettings] = useState({
    enabled: false,
    send_time: '09:00',
    use_participant_timezone: true,
    message_template: `Hey {first_name}, you are now eligible to return to The HELP Center for a return food box during this eligibility window: {window_start} through {window_end}.

When you come, just go to the check-in kiosk and select 'I've Been Here Before.'

If you don't need a box at this time, we are here in the future if you need us.`
  });
  const [smsNotifications, setSmsNotifications] = useState([]);
  const [smsStatusCounts, setSmsStatusCounts] = useState({});
  const [loadingSms, setLoadingSms] = useState(false);
  const [savingSms, setSavingSms] = useState(false);
  const [processingNotifications, setProcessingNotifications] = useState(false);
  const [smsActiveTab, setSmsActiveTab] = useState('settings');

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/food-pantry/settings`);
      const { settings: fetchedSettings, locations: fetchedLocations } = response.data;
      
      setSettings(fetchedSettings);
      setLocations(fetchedLocations || []);
      
      if (fetchedSettings) {
        setFormData({
          kiosk_enabled: fetchedSettings.kiosk_enabled ?? true,
          lobby_display_message: fetchedSettings.lobby_display_message || "Please meet us at the door to have assistance taking your box to your car",
          now_serving_alert_seconds: fetchedSettings.now_serving_alert_seconds || 60,
          verification_field: fetchedSettings.verification_field || 'date_of_birth',
          service_days: fetchedSettings.service_days || [0, 1, 2],
          duplicate_window_minutes: fetchedSettings.duplicate_window_minutes || 30,
          form_reset_seconds: fetchedSettings.form_reset_seconds || 10,
          confirmation_message: fetchedSettings.confirmation_message || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchSmsSettings();
    fetchSmsNotifications();
  }, []);

  const fetchSmsSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/food-pantry/sms-settings`);
      if (response.data) {
        setSmsSettings({
          enabled: response.data.enabled ?? false,
          send_time: response.data.send_time || '09:00',
          use_participant_timezone: response.data.use_participant_timezone ?? true,
          message_template: response.data.message_template || smsSettings.message_template
        });
      }
    } catch (error) {
      console.error('Failed to fetch SMS settings:', error);
    }
  };

  const fetchSmsNotifications = async (status = null) => {
    setLoadingSms(true);
    try {
      let url = `${API}/admin/food-pantry/sms-notifications?limit=50`;
      if (status) url += `&status=${status}`;
      const response = await axios.get(url);
      setSmsNotifications(response.data.notifications || []);
      setSmsStatusCounts(response.data.status_counts || {});
    } catch (error) {
      console.error('Failed to fetch SMS notifications:', error);
    } finally {
      setLoadingSms(false);
    }
  };

  const handleSaveSmsSettings = async () => {
    setSavingSms(true);
    try {
      await axios.put(`${API}/admin/food-pantry/sms-settings`, smsSettings);
      toast.success('SMS notification settings saved');
    } catch (error) {
      console.error('Failed to save SMS settings:', error);
      toast.error('Failed to save SMS settings');
    } finally {
      setSavingSms(false);
    }
  };

  const handleTriggerProcessing = async () => {
    setProcessingNotifications(true);
    try {
      const response = await axios.post(`${API}/admin/food-pantry/sms-notifications/process`);
      toast.success(`Processing complete: ${response.data.scheduled || 0} notifications scheduled`);
      fetchSmsNotifications();
    } catch (error) {
      console.error('Failed to trigger processing:', error);
      toast.error('Failed to process notifications');
    } finally {
      setProcessingNotifications(false);
    }
  };

  const handleSendDueNotifications = async () => {
    try {
      const response = await axios.post(`${API}/admin/food-pantry/sms-notifications/send-due`);
      toast.success(`Sent ${response.data.sent || 0} notifications`);
      fetchSmsNotifications();
    } catch (error) {
      console.error('Failed to send notifications:', error);
      toast.error('Failed to send due notifications');
    }
  };

  const handleResendNotification = async (notificationId) => {
    try {
      await axios.post(`${API}/admin/food-pantry/sms-notifications/${notificationId}/resend`);
      toast.success('Notification resent');
      fetchSmsNotifications();
    } catch (error) {
      console.error('Failed to resend notification:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend notification');
    }
  };

  const formatDateTimeLocal = (dateStr) => {
    if (!dateStr) return '-';
    return formatDateTime(dateStr) || dateStr;
  };

  const getNotificationStatusBadge = (status) => {
    const statusStyles = {
      scheduled: 'bg-blue-100 text-blue-800',
      sent: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      pending_timezone: 'bg-yellow-100 text-yellow-800',
      skipped: 'bg-gray-100 text-gray-800'
    };
    return (
      <Badge className={statusStyles[status] || 'bg-gray-100 text-gray-800'}>
        {status?.replace(/_/g, ' ') || 'Unknown'}
      </Badge>
    );
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/food-pantry/settings`, formData);
      toast.success('Settings saved successfully');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleServiceDayToggle = (day) => {
    setFormData(prev => {
      const days = prev.service_days.includes(day)
        ? prev.service_days.filter(d => d !== day)
        : [...prev.service_days, day].sort();
      return { ...prev, service_days: days };
    });
  };

  const handleOpenLocationDialog = (location = null) => {
    if (location) {
      setEditingLocation(location);
      setLocationForm({
        name: location.name,
        address: location.address || '',
        is_active: location.is_active ?? true,
        teams_webhook_url: location.teams_webhook_url || '',
        eligibility_rule: location.eligibility_rule || 'monthly',
        operational_mode: location.operational_mode || (location.id === 'fp-charlotte' ? 'charlotte' : 'nashville')
      });
    } else {
      setEditingLocation(null);
      setLocationForm({
        name: '',
        address: '',
        is_active: true,
        teams_webhook_url: '',
        eligibility_rule: 'monthly',
        operational_mode: 'nashville'
      });
    }
    setShowLocationDialog(true);
  };

  const handleSaveLocation = async () => {
    if (!locationForm.name.trim()) {
      toast.error('Location name is required');
      return;
    }
    
    setSavingLocation(true);
    try {
      if (editingLocation) {
        await axios.put(`${API}/admin/food-pantry/locations/${editingLocation.id}`, locationForm);
        toast.success('Location updated successfully');
      } else {
        await axios.post(`${API}/admin/food-pantry/locations`, locationForm);
        toast.success('Location created successfully');
      }
      setShowLocationDialog(false);
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save location');
    } finally {
      setSavingLocation(false);
    }
  };

  const copyKioskUrl = (locationId) => {
    const url = `${window.location.origin}/checkin?location=${locationId}`;
    navigator.clipboard.writeText(url);
    toast.success('Kiosk URL copied to clipboard');
  };

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="food-pantry-admin-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Food Pantry Check-In Settings</h1>
          <p className="text-muted-foreground">Configure the kiosk check-in system</p>
        </div>
        <Button onClick={handleSaveSettings} disabled={saving}>
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Settings
        </Button>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="locations">Locations</TabsTrigger>
          <TabsTrigger value="eligibility">Eligibility</TabsTrigger>
          <TabsTrigger value="sms">SMS Notifications</TabsTrigger>
          <TabsTrigger value="notifications">Teams</TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Kiosk Settings
              </CardTitle>
              <CardDescription>Configure the public-facing kiosk behavior</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="kiosk_enabled" className="text-base font-medium">Enable Kiosk Check-In</Label>
                  <p className="text-sm text-muted-foreground">Allow clients to self-check-in via tablet</p>
                </div>
                <Switch
                  id="kiosk_enabled"
                  checked={formData.kiosk_enabled}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, kiosk_enabled: checked }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="verification_field">Verification Method for Returning Clients</Label>
                <select
                  id="verification_field"
                  value={formData.verification_field}
                  onChange={(e) => setFormData(prev => ({ ...prev, verification_field: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="date_of_birth">Date of Birth</option>
                  <option value="last_four_ssn">Last 4 of SSN</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label>Service Days (Eligibility Windows)</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Select which days clients can pick up food boxes
                </p>
                <div className="flex flex-wrap gap-2">
                  {dayNames.map((day, index) => (
                    <Button
                      key={day}
                      type="button"
                      variant={formData.service_days.includes(index) ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleServiceDayToggle(index)}
                    >
                      {day.slice(0, 3)}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="duplicate_window">Duplicate Prevention Window (minutes)</Label>
                  <Input
                    id="duplicate_window"
                    type="number"
                    min="5"
                    max="120"
                    value={formData.duplicate_window_minutes}
                    onChange={(e) => setFormData(prev => ({ ...prev, duplicate_window_minutes: parseInt(e.target.value) || 30 }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Prevents duplicate check-ins within this time window
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reset_seconds">Form Reset Delay (seconds)</Label>
                  <Input
                    id="reset_seconds"
                    type="number"
                    min="5"
                    max="60"
                    value={formData.form_reset_seconds}
                    onChange={(e) => setFormData(prev => ({ ...prev, form_reset_seconds: parseInt(e.target.value) || 10 }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Time before kiosk resets after successful check-in
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmation">Confirmation Message</Label>
                <Textarea
                  id="confirmation"
                  value={formData.confirmation_message}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmation_message: e.target.value }))}
                  placeholder="Thank you. Your check-in has been submitted. A staff member will assist you shortly."
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  Message shown after successful check-in
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Lobby Display Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ExternalLink className="h-5 w-5" />
                Lobby Display Screen
              </CardTitle>
              <CardDescription>Configure the queue display for your lobby TV</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Nashville Lobby Display */}
              <div className="p-4 bg-muted rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Nashville Lobby Display URL</Label>
                    <p className="text-sm text-muted-foreground">Open this URL on your Nashville lobby TV</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText('https://myhelp.thehelpcentertn.org/lobby-display');
                        toast.success('URL copied to clipboard');
                      }}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open('/lobby-display', '_blank')}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Preview
                    </Button>
                  </div>
                </div>
                <code className="block text-sm bg-background px-3 py-2 rounded border">
                  https://myhelp.thehelpcentertn.org/lobby-display
                </code>
              </div>

              {/* Charlotte Lobby Display */}
              <div className="p-4 bg-muted rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Charlotte Lobby Display URL</Label>
                    <p className="text-sm text-muted-foreground">Open this URL on your Charlotte lobby TV</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText('https://myhelp.thehelpcentertn.org/lobby-display-charlotte');
                        toast.success('URL copied to clipboard');
                      }}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open('/lobby-display-charlotte', '_blank')}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Preview
                    </Button>
                  </div>
                </div>
                <code className="block text-sm bg-background px-3 py-2 rounded border">
                  https://myhelp.thehelpcentertn.org/lobby-display-charlotte
                </code>
              </div>

              {/* Nashville Kiosk */}
              <div className="p-4 bg-muted rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Nashville Kiosk URL</Label>
                    <p className="text-sm text-muted-foreground">Client check-in kiosk for Nashville</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText('https://myhelp.thehelpcentertn.org/checkin');
                        toast.success('URL copied to clipboard');
                      }}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open('/checkin', '_blank')}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Preview
                    </Button>
                  </div>
                </div>
                <code className="block text-sm bg-background px-3 py-2 rounded border">
                  https://myhelp.thehelpcentertn.org/checkin
                </code>
              </div>

              {/* Charlotte Kiosk */}
              <div className="p-4 bg-muted rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Charlotte Kiosk URL</Label>
                    <p className="text-sm text-muted-foreground">Client check-in kiosk for Charlotte</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText('https://myhelp.thehelpcentertn.org/checkin-charlotte');
                        toast.success('URL copied to clipboard');
                      }}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open('/checkin-charlotte', '_blank')}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Preview
                    </Button>
                  </div>
                </div>
                <code className="block text-sm bg-background px-3 py-2 rounded border">
                  https://myhelp.thehelpcentertn.org/checkin-charlotte
                </code>
              </div>

              {/* Nashville Settings Section */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-blue-600">Nashville Settings</h3>
                </div>
                
                <div className="space-y-4 pl-4 border-l-2 border-blue-200">
                  <div className="space-y-2">
                    <Label htmlFor="nashville_lobby_message">&quot;Now Serving&quot; Display Message</Label>
                    <Textarea
                      id="nashville_lobby_message"
                      value={formData.nashville?.lobby_display_message || formData.lobby_display_message}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        nashville: { ...prev.nashville, lobby_display_message: e.target.value },
                        lobby_display_message: e.target.value
                      }))}
                      placeholder="Please meet us at the door to have assistance taking your box to your car"
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      Message shown on the Nashville lobby display when calling a client
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="nashville_alert_duration">&quot;Now Serving&quot; Alert Duration (seconds)</Label>
                    <Input
                      id="nashville_alert_duration"
                      type="number"
                      min="10"
                      max="300"
                      value={formData.nashville?.alert_duration || formData.now_serving_alert_seconds}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        nashville: { ...prev.nashville, alert_duration: parseInt(e.target.value) || 60 },
                        now_serving_alert_seconds: parseInt(e.target.value) || 60
                      }))}
                      className="w-32"
                    />
                    <p className="text-xs text-muted-foreground">
                      How long the &quot;Now Serving&quot; alert stays visible (10-300 seconds)
                    </p>
                  </div>
                </div>
              </div>

              {/* Charlotte Settings Section */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-purple-600">Charlotte Settings</h3>
                </div>
                
                <div className="space-y-4 pl-4 border-l-2 border-purple-200">
                  <div className="space-y-2">
                    <Label htmlFor="charlotte_lobby_message">&quot;Now Serving&quot; Display Message</Label>
                    <Textarea
                      id="charlotte_lobby_message"
                      value={formData.charlotte?.lobby_display_message || "Please meet us at the door to have assistance taking your box to your car"}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        charlotte: { ...prev.charlotte, lobby_display_message: e.target.value }
                      }))}
                      placeholder="Please meet us at the door to have assistance taking your box to your car"
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      Message shown on the Charlotte lobby display when calling a client
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="charlotte_alert_duration">&quot;Now Serving&quot; Alert Duration (seconds)</Label>
                    <Input
                      id="charlotte_alert_duration"
                      type="number"
                      min="10"
                      max="300"
                      value={formData.charlotte?.alert_duration || 60}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        charlotte: { ...prev.charlotte, alert_duration: parseInt(e.target.value) || 60 }
                      }))}
                      className="w-32"
                    />
                    <p className="text-xs text-muted-foreground">
                      How long the &quot;Now Serving&quot; alert stays visible (10-300 seconds)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="charlotte_staff_alert">Pantry Staff Alert Message</Label>
                    <Textarea
                      id="charlotte_staff_alert"
                      value={formData.charlotte?.pantry_staff_alert_message || "Attention Pantry Staff, you have a shopper coming to the pantry"}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        charlotte: { ...prev.charlotte, pantry_staff_alert_message: e.target.value }
                      }))}
                      placeholder="Attention Pantry Staff, you have a shopper coming to the pantry"
                      rows={2}
                    />
                    <p className="text-xs text-muted-foreground">
                      Audible alert message played when calling a participant to the pantry (text-to-speech)
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Locations */}
        <TabsContent value="locations" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Food Pantry Locations
                </CardTitle>
                <CardDescription>Manage check-in kiosk locations</CardDescription>
              </div>
              <Button onClick={() => handleOpenLocationDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Add Location
              </Button>
            </CardHeader>
            <CardContent>
              {locations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No locations configured yet</p>
                  <Button variant="outline" className="mt-4" onClick={() => handleOpenLocationDialog()}>
                    Add Your First Location
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {locations.map(location => (
                    <Card key={location.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium">{location.name}</h3>
                              <Badge variant={location.is_active ? "default" : "secondary"}>
                                {location.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                              <Badge 
                                variant="outline" 
                                className={
                                  (location.operational_mode === 'charlotte' || (!location.operational_mode && location.id === 'fp-charlotte'))
                                    ? 'bg-purple-50 text-purple-700 border-purple-200'
                                    : 'bg-blue-50 text-blue-700 border-blue-200'
                                }
                              >
                                {(location.operational_mode === 'charlotte' || (!location.operational_mode && location.id === 'fp-charlotte'))
                                  ? 'Charlotte Mode'
                                  : 'Nashville Mode'}
                              </Badge>
                            </div>
                            {location.address && (
                              <p className="text-sm text-muted-foreground">{location.address}</p>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              <code className="text-xs bg-muted px-2 py-1 rounded">
                                {window.location.origin}/checkin?location={location.id}
                              </code>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => copyKioskUrl(location.id)}
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => window.open(`/checkin?location=${location.id}`, '_blank')}
                              >
                                <ExternalLink className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => handleOpenLocationDialog(location)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Eligibility Rules */}
        <TabsContent value="eligibility" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Eligibility Rules
              </CardTitle>
              <CardDescription>Configure eligibility rules for each location. Rules determine how frequently participants can receive food box distributions.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-3">Available Rule Types</h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="p-4 bg-background rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Monthly</Badge>
                      <span className="font-medium">30-Day Rule</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Participants are eligible 30 days after their last qualifying distribution. 
                      The system looks for case notes of type "Returning Box Distribution" or "New Applicant Intake & Enrollment/Initial Box" 
                      and calculates the next eligible date based on a 30-day window.
                    </p>
                  </div>
                  <div className="p-4 bg-background rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Weekly</Badge>
                      <span className="font-medium">Weekly Rule</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Participants can receive one distribution per operating week (Mon-Wed). 
                      If a qualifying case note exists within the current Mon-Wed period, the participant is ineligible until the next week.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Location Eligibility Rules</h4>
                  <p className="text-xs text-muted-foreground">
                    Each location can have its own eligibility rule. Click Edit to change a location's rule.
                  </p>
                </div>

                {locations.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground border rounded-lg">
                    <p>No locations configured. Add locations in the Locations tab first.</p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[200px]">Location</TableHead>
                          <TableHead>Eligibility Rule</TableHead>
                          <TableHead className="w-[300px]">Description</TableHead>
                          <TableHead className="w-[80px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {locations.map((location) => (
                          <TableRow key={location.id}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-muted-foreground" />
                                {location.name}
                              </div>
                            </TableCell>
                            <TableCell>
                              {location.eligibility_rule === 'weekly' ? (
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                  <Calendar className="h-3 w-3 mr-1" />
                                  Weekly
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                  <Calendar className="h-3 w-3 mr-1" />
                                  Monthly (30-Day)
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {location.eligibility_rule === 'weekly' 
                                ? 'One distribution per Mon-Wed operating week'
                                : '30 days between distributions'}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenLocationDialog(location)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              <div className="p-4 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200">
                <h5 className="font-medium text-amber-700 dark:text-amber-400 mb-1">Important Note</h5>
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  Changing a location's eligibility rule takes effect immediately for all new check-ins at that location. 
                  Existing check-in records are not affected. The automatic case note creation behavior remains unchanged for both rule types.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMS Notifications */}
        <TabsContent value="sms" className="space-y-6">
          <Tabs value={smsActiveTab} onValueChange={setSmsActiveTab} className="space-y-4">
            <TabsList>
              <TabsTrigger value="settings">Settings</TabsTrigger>
              <TabsTrigger value="scheduled">Scheduled ({smsStatusCounts.scheduled || 0})</TabsTrigger>
              <TabsTrigger value="sent">Sent ({smsStatusCounts.sent || 0})</TabsTrigger>
              <TabsTrigger value="failed">Failed ({smsStatusCounts.failed || 0})</TabsTrigger>
              <TabsTrigger value="pending_timezone">Missing TZ ({smsStatusCounts.pending_timezone || 0})</TabsTrigger>
            </TabsList>

            {/* SMS Settings Tab */}
            <TabsContent value="settings" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    Automatic Return Eligibility SMS
                  </CardTitle>
                  <CardDescription>
                    Automatically notify participants when they become eligible for their next food box
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <Label className="text-base font-medium">Enable Automatic SMS Notifications</Label>
                      <p className="text-sm text-muted-foreground">
                        Send SMS to participants when they enter their eligibility window
                      </p>
                    </div>
                    <Switch
                      checked={smsSettings.enabled}
                      onCheckedChange={(checked) => setSmsSettings(prev => ({ ...prev, enabled: checked }))}
                      data-testid="sms-enabled-toggle"
                    />
                  </div>

                  {smsSettings.enabled && (
                    <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                        <CheckCircle className="h-5 w-5" />
                        <span className="font-medium">SMS notifications are enabled</span>
                      </div>
                      <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                        Participants will receive notifications at 9:00 AM in their local timezone
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="send_time">Send Time</Label>
                      <Input
                        id="send_time"
                        type="time"
                        value={smsSettings.send_time}
                        onChange={(e) => setSmsSettings(prev => ({ ...prev, send_time: e.target.value }))}
                        data-testid="sms-send-time"
                      />
                      <p className="text-xs text-muted-foreground">
                        Time to send notifications (participant&apos;s local time)
                      </p>
                    </div>

                    <div className="space-y-2 flex items-center">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <Label>Use Participant Local Time Zone</Label>
                          <p className="text-xs text-muted-foreground">
                            Send at {formatTime12(smsSettings.send_time)} in each participant&apos;s time zone
                          </p>
                        </div>
                        <Switch
                          checked={smsSettings.use_participant_timezone}
                          onCheckedChange={(checked) => setSmsSettings(prev => ({ ...prev, use_participant_timezone: checked }))}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message_template">SMS Message Template</Label>
                    <Textarea
                      id="message_template"
                      value={smsSettings.message_template}
                      onChange={(e) => setSmsSettings(prev => ({ ...prev, message_template: e.target.value }))}
                      rows={8}
                      placeholder="Message template..."
                      data-testid="sms-message-template"
                    />
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p>Available merge fields:</p>
                      <ul className="list-disc list-inside ml-2">
                        <li><code>{'{first_name}'}</code> - Participant&apos;s first name</li>
                        <li><code>{'{window_start}'}</code> - Eligibility window start date</li>
                        <li><code>{'{window_end}'}</code> - Eligibility window end date</li>
                      </ul>
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={handleTriggerProcessing}
                      disabled={processingNotifications}
                      data-testid="process-notifications-btn"
                    >
                      {processingNotifications ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4 mr-2" />
                      )}
                      Process Eligibility Now
                    </Button>
                    <Button onClick={handleSaveSmsSettings} disabled={savingSms} data-testid="save-sms-settings-btn">
                      {savingSms ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Save SMS Settings
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Scheduled Notifications Tab */}
            <TabsContent value="scheduled">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      Scheduled Notifications
                    </CardTitle>
                    <CardDescription>SMS notifications waiting to be sent</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => fetchSmsNotifications('scheduled')}>
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Refresh
                    </Button>
                    <Button size="sm" onClick={handleSendDueNotifications} data-testid="send-due-btn">
                      <Send className="h-4 w-4 mr-1" />
                      Send Due Now
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingSms ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : smsNotifications.filter(n => n.status === 'scheduled').length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No scheduled notifications</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Participant</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead>Eligibility Window</TableHead>
                          <TableHead>Scheduled For</TableHead>
                          <TableHead>Timezone</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {smsNotifications.filter(n => n.status === 'scheduled').map(notification => (
                          <TableRow key={notification.id}>
                            <TableCell className="font-medium">{notification.participant_name}</TableCell>
                            <TableCell>{notification.phone_number}</TableCell>
                            <TableCell>{notification.window_start} - {notification.window_end}</TableCell>
                            <TableCell>{formatDateTime(notification.scheduled_local)}</TableCell>
                            <TableCell>{notification.participant_timezone || '-'}</TableCell>
                            <TableCell>{getNotificationStatusBadge(notification.status)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Sent Notifications Tab */}
            <TabsContent value="sent">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      Sent Notifications
                    </CardTitle>
                    <CardDescription>Successfully delivered SMS notifications</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => fetchSmsNotifications('sent')}>
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Refresh
                  </Button>
                </CardHeader>
                <CardContent>
                  {loadingSms ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : smsNotifications.filter(n => n.status === 'sent').length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Send className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No sent notifications yet</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Participant</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead>Eligibility Window</TableHead>
                          <TableHead>Sent At</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {smsNotifications.filter(n => n.status === 'sent').map(notification => (
                          <TableRow key={notification.id}>
                            <TableCell className="font-medium">{notification.participant_name}</TableCell>
                            <TableCell>{notification.phone_number}</TableCell>
                            <TableCell>{notification.window_start} - {notification.window_end}</TableCell>
                            <TableCell>{formatDateTime(notification.sent_at)}</TableCell>
                            <TableCell>{getNotificationStatusBadge(notification.status)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Failed Notifications Tab */}
            <TabsContent value="failed">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                      Failed Notifications
                    </CardTitle>
                    <CardDescription>Notifications that could not be delivered</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => fetchSmsNotifications('failed')}>
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Refresh
                  </Button>
                </CardHeader>
                <CardContent>
                  {loadingSms ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : smsNotifications.filter(n => n.status === 'failed').length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No failed notifications</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Participant</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead>Error</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {smsNotifications.filter(n => n.status === 'failed').map(notification => (
                          <TableRow key={notification.id}>
                            <TableCell className="font-medium">{notification.participant_name}</TableCell>
                            <TableCell>{notification.phone_number}</TableCell>
                            <TableCell className="text-red-600 text-sm">{notification.error}</TableCell>
                            <TableCell>{formatDateTime(notification.created_at)}</TableCell>
                            <TableCell>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleResendNotification(notification.id)}
                              >
                                <RefreshCw className="h-3 w-3 mr-1" />
                                Retry
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Missing Timezone Tab */}
            <TabsContent value="pending_timezone">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5 text-yellow-600" />
                    Missing Time Zone
                  </CardTitle>
                  <CardDescription>
                    Participants whose time zone could not be determined from their address
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingSms ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : smsNotifications.filter(n => n.status === 'pending_timezone').length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>All participants have valid time zones</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-4 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200">
                        <p className="text-sm text-yellow-700 dark:text-yellow-400">
                          <AlertTriangle className="h-4 w-4 inline mr-2" />
                          These participants need their address updated with a valid state or ZIP code to determine their time zone.
                        </p>
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Participant</TableHead>
                            <TableHead>Phone</TableHead>
                            <TableHead>Eligibility Window</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {smsNotifications.filter(n => n.status === 'pending_timezone').map(notification => (
                            <TableRow key={notification.id}>
                              <TableCell className="font-medium">{notification.participant_name}</TableCell>
                              <TableCell>{notification.phone_number}</TableCell>
                              <TableCell>{notification.window_start} - {notification.window_end}</TableCell>
                              <TableCell>{getNotificationStatusBadge(notification.status)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Notifications */}
        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Microsoft Teams Integration
              </CardTitle>
              <CardDescription>Configure webhook notifications for check-ins per location</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-muted rounded-lg mb-4">
                <h4 className="font-medium mb-2">Notification Contents</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Client name and check-in type (new/returning)</li>
                  <li>• Participant match status</li>
                  <li>• Eligibility status and return date</li>
                  <li>• Case note creation status</li>
                  <li>• Color-coded by status (green=eligible, red=not eligible, orange=needs review)</li>
                </ul>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Location Webhooks</h4>
                  <p className="text-xs text-muted-foreground">
                    Each location can have its own Teams webhook URL. Notifications are sent to the location-specific webhook when a check-in occurs.
                  </p>
                </div>

                {locations.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground border rounded-lg">
                    <p>No locations configured. Add locations in the Locations tab first.</p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[200px]">Location</TableHead>
                          <TableHead>Teams Webhook URL</TableHead>
                          <TableHead className="w-[100px]">Status</TableHead>
                          <TableHead className="w-[80px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {locations.map((location) => (
                          <TableRow key={location.id}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-muted-foreground" />
                                {location.name}
                              </div>
                            </TableCell>
                            <TableCell>
                              {location.teams_webhook_url ? (
                                <div className="flex items-center gap-2">
                                  <code className="text-xs bg-muted px-2 py-1 rounded max-w-[300px] truncate block">
                                    {location.teams_webhook_url}
                                  </code>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      navigator.clipboard.writeText(location.teams_webhook_url);
                                      toast.success('Webhook URL copied to clipboard');
                                    }}
                                  >
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-sm">Not configured</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {location.teams_webhook_url ? (
                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Active
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  No Webhook
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenLocationDialog(location)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200">
                  <h5 className="font-medium text-blue-700 dark:text-blue-400 mb-1">How it works</h5>
                  <p className="text-sm text-blue-600 dark:text-blue-400">
                    When a check-in occurs at a location, the notification is sent to that location's configured webhook. 
                    If no webhook is configured for a location, no notification will be sent for check-ins at that location.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Location Dialog */}
      <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingLocation ? 'Edit Location' : 'Add Location'}</DialogTitle>
            <DialogDescription>
              {editingLocation ? 'Update location details' : 'Create a new food pantry location'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="location_name">Location Name *</Label>
              <Input
                id="location_name"
                value={locationForm.name}
                onChange={(e) => setLocationForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Nashville Food Pantry"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location_address">Address</Label>
              <Input
                id="location_address"
                value={locationForm.address}
                onChange={(e) => setLocationForm(prev => ({ ...prev, address: e.target.value }))}
                placeholder="123 Main St, Nashville, TN"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location_webhook">Teams Webhook URL</Label>
              <Input
                id="location_webhook"
                type="url"
                value={locationForm.teams_webhook_url}
                onChange={(e) => setLocationForm(prev => ({ ...prev, teams_webhook_url: e.target.value }))}
                placeholder="https://..."
              />
              <p className="text-xs text-muted-foreground">
                Paste your Power Automate or Teams Incoming Webhook URL for this location. 
                If not configured, no Teams notification will be sent for check-ins at this location.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Eligibility Rule</Label>
              <Select 
                value={locationForm.eligibility_rule} 
                onValueChange={(value) => setLocationForm(prev => ({ ...prev, eligibility_rule: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select eligibility rule" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">
                    <div className="flex items-center gap-2">
                      <span>Monthly (30-Day Rule)</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="weekly">
                    <div className="flex items-center gap-2">
                      <span>Weekly (One per Mon-Wed)</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {locationForm.eligibility_rule === 'weekly' 
                  ? 'Participants can receive one distribution per Mon-Wed operating week.'
                  : 'Participants are eligible 30 days after their last distribution.'}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Operational Mode</Label>
              <Select 
                value={locationForm.operational_mode} 
                onValueChange={(value) => setLocationForm(prev => ({ ...prev, operational_mode: value }))}
                data-testid="operational-mode-select"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select operational mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nashville">
                    <div className="flex items-center gap-2">
                      <span>Nashville Mode</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="charlotte">
                    <div className="flex items-center gap-2">
                      <span>Charlotte Mode</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-muted-foreground space-y-1 mt-2 p-2 bg-muted rounded">
                {locationForm.operational_mode === 'charlotte' ? (
                  <>
                    <p className="font-medium text-purple-600">Charlotte Mode:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>Lobby display updates only when Call is pressed</li>
                      <li>Audible pantry staff alert only (no Now Serving display)</li>
                      <li>Simplified tablet: queue totals + Complete buttons only</li>
                      <li>All timestamps in Eastern Time (ET)</li>
                    </ul>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-blue-600">Nashville Mode:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>Automatic lobby display on check-in</li>
                      <li>Now Serving alert with display message</li>
                      <li>Full tablet: Now Serving, Next Call, Call Again, Jump To</li>
                      <li>Car-side box delivery flow</li>
                    </ul>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="location_active">Active</Label>
                <p className="text-sm text-muted-foreground">Allow check-ins at this location</p>
              </div>
              <Switch
                id="location_active"
                checked={locationForm.is_active}
                onCheckedChange={(checked) => setLocationForm(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLocationDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveLocation} disabled={savingLocation}>
              {savingLocation ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              {editingLocation ? 'Update' : 'Create'} Location
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FoodPantryAdminSettingsPage;
